import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, ShoppingBag, CheckCircle, XCircle, ClockClockwise, Handshake, Package,
  Warning, TrashSimple, Buildings,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";

const UNITS = ["adet", "kg", "ton", "m", "m²", "m³", "lt", "torba", "paket", "palet"];
const STATUS_META = {
  taslak: { label: "Taslak", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  teklif_bekliyor: { label: "Teklif Bekliyor", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  onay_bekliyor: { label: "Onay Bekliyor", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  onaylandi: { label: "Onaylandı", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  reddedildi: { label: "Reddedildi", cls: "bg-red-100 text-red-700 border-red-300" },
  siparis_verildi: { label: "Sipariş Verildi", cls: "bg-[#1B4776] text-white border-[#1B4776]" },
};
const PRIORITY_META = {
  dusuk: { label: "Düşük", cls: "text-slate-600" },
  orta: { label: "Orta", cls: "text-slate-800" },
  yuksek: { label: "Yüksek", cls: "text-amber-700" },
  acil: { label: "Acil", cls: "text-red-700 font-bold" },
};

const emptyRequest = {
  site_id: "", site_name: "", material_name: "", quantity: "", unit: "adet",
  need_by_date: "", priority: "orta", notes: "",
};
const emptyOffer = {
  supplier_id: "", supplier_name: "", unit_price: "", delivery_days: "",
  valid_until: "", notes: "",
};

export default function Purchase() {
  const { user } = useAuth();
  const { sites, activeSiteId } = useSites();
  const askConfirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqForm, setReqForm] = useState(emptyRequest);
  const [offerFor, setOfferFor] = useState(null);   // pending offers for a request
  const [offerForm, setOfferForm] = useState(emptyOffer);
  const [approveFor, setApproveFor] = useState(null);
  const [approveIdx, setApproveIdx] = useState(0);
  const [approveNotes, setApproveNotes] = useState("");
  const isAdmin = !!user;

  const load = async () => {
    const params = {};
    if (activeSiteId && activeSiteId !== "all") params.site_id = activeSiteId;
    try {
      const { data } = await axios.get(`${API}/purchase/requests`, { params });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [activeSiteId]); // eslint-disable-line

  const stats = useMemo(() => ({
    total: rows.length,
    waiting_offer: rows.filter(r => r.status === "teklif_bekliyor").length,
    waiting_approval: rows.filter(r => r.status === "onay_bekliyor").length,
    ordered: rows.filter(r => r.status === "siparis_verildi").length,
  }), [rows]);

  const openNewRequest = () => {
    const activeSite = activeSiteId && activeSiteId !== "all" ? sites.find(s => s.id === activeSiteId) : null;
    setReqForm({ ...emptyRequest, site_id: activeSite?.id || "", site_name: activeSite?.name || "" });
    setReqOpen(true);
  };

  const saveRequest = async () => {
    if (!reqForm.site_id || !reqForm.material_name || !reqForm.quantity) {
      toast.error("Şantiye, malzeme ve miktar zorunlu");
      return;
    }
    try {
      await axios.post(`${API}/purchase/requests`, {
        ...reqForm,
        quantity: parseFloat(reqForm.quantity) || 0,
      });
      toast.success("Talep oluşturuldu");
      setReqOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const addOffer = async () => {
    if (!offerForm.supplier_id || !offerForm.unit_price) {
      toast.error("Firma ve birim fiyat zorunlu");
      return;
    }
    try {
      await axios.post(`${API}/purchase/requests/${offerFor.id}/offers`, {
        ...offerForm,
        unit_price: parseFloat(offerForm.unit_price) || 0,
        delivery_days: parseInt(offerForm.delivery_days) || 0,
      });
      toast.success("Teklif eklendi");
      setOfferForm(emptyOffer);
      load();
      // refresh the sheet — pick updated version
      const { data } = await axios.get(`${API}/purchase/requests`);
      const updated = data.find(x => x.id === offerFor.id);
      if (updated) setOfferFor(updated);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const delOffer = async (prId, idx) => {
    const ok = await askConfirm({
      title: "Teklifi Sil",
      description: "Bu teklifi silmek istiyor musunuz? Bu işlem geri alınamaz.",
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/purchase/requests/${prId}/offers/${idx}`);
      const { data } = await axios.get(`${API}/purchase/requests`);
      const updated = data.find(x => x.id === prId);
      if (updated) setOfferFor(updated);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const approve = async () => {
    try {
      await axios.post(`${API}/purchase/requests/${approveFor.id}/approve`, {
        selected_offer_index: approveIdx,
        approval_notes: approveNotes,
      });
      toast.success("Onaylandı — sözleşme oluşturuldu");
      setApproveFor(null); setApproveNotes(""); setApproveIdx(0);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const reject = async (pr) => {
    const ok = await askConfirm({
      title: "Talebi Reddet",
      description: `${pr.code} talebini reddetmek istiyor musunuz?`,
      destructive: true,
      confirmText: "Reddet",
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/purchase/requests/${pr.id}/reject`);
      toast.success("Reddedildi");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Shell
      title="Satın Alma"
      actions={
        <Dialog open={reqOpen} onOpenChange={setReqOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNewRequest} data-testid="add-request"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              <Plus size={16} className="mr-2" /> Yeni Talep
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:rounded-sm max-w-lg">
            <DialogHeader><DialogTitle className="font-bold font-heading">Yeni Malzeme Talebi</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Şantiye</Label>
                <Select value={reqForm.site_id} onValueChange={(v) => {
                  const s = sites.find(x => x.id === v);
                  setReqForm(f => ({ ...f, site_id: v, site_name: s?.name || "" }));
                }}>
                  <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="req-site">
                    <SelectValue placeholder="Şantiye seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.filter(s => s.status === "aktif").map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Malzeme</Label>
                <Input value={reqForm.material_name} data-testid="req-material"
                       onChange={e => setReqForm(f => ({ ...f, material_name: e.target.value }))}
                       placeholder="Örn: Demir 12mm"
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Miktar</Label>
                <Input type="number" step="0.01" value={reqForm.quantity} data-testid="req-qty"
                       onChange={e => setReqForm(f => ({ ...f, quantity: e.target.value }))}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Birim</Label>
                <Select value={reqForm.unit} onValueChange={(v) => setReqForm(f => ({ ...f, unit: v }))}>
                  <SelectTrigger className="mt-2 rounded-none sm:rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">İhtiyaç Tarihi</Label>
                <Input type="date" value={reqForm.need_by_date}
                       onChange={e => setReqForm(f => ({ ...f, need_by_date: e.target.value }))}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Öncelik</Label>
                <Select value={reqForm.priority} onValueChange={(v) => setReqForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger className="mt-2 rounded-none sm:rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Not</Label>
                <Textarea value={reqForm.notes} onChange={e => setReqForm(f => ({ ...f, notes: e.target.value }))}
                          rows={2} className="mt-2 rounded-none sm:rounded-sm" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={saveRequest} data-testid="save-request"
                      className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                Talebi Aç
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Toplam</div>
          <div className="text-2xl font-bold text-slate-900 font-heading">{stats.total}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Teklif Bekliyor</div>
          <div className="text-2xl font-bold text-amber-700 font-heading">{stats.waiting_offer}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-blue-700">Onay Bekliyor</div>
          <div className="text-2xl font-bold text-blue-700 font-heading">{stats.waiting_approval}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B4776]">Sipariş Verildi</div>
          <div className="text-2xl font-bold text-[#1B4776] font-heading">{stats.ordered}</div>
        </Card>
      </div>

      {/* Requests list */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rows.map((pr) => {
          const st = STATUS_META[pr.status] || STATUS_META.taslak;
          const pr_meta = PRIORITY_META[pr.priority] || PRIORITY_META.orta;
          const canOffer = !!user
                            && ["teklif_bekliyor", "onay_bekliyor"].includes(pr.status);
          const canApprove = isAdmin && pr.status === "onay_bekliyor" && (pr.offers || []).length > 0;
          return (
            <Card key={pr.id} data-testid={`pr-${pr.id}`}
                  className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 bg-[#1B4776] flex items-center justify-center flex-shrink-0">
                    <ShoppingBag size={22} weight="duotone" className="text-[#95C11F]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">{pr.code}</div>
                    <div className="text-base font-bold text-slate-900 font-heading leading-tight">{pr.material_name}</div>
                    <div className="text-xs text-slate-500 mt-1 font-mono">{pr.quantity} {pr.unit}</div>
                  </div>
                </div>
                <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-1 border ${st.cls}`}>
                  {st.label}
                </span>
              </div>

              <div className="text-xs text-slate-600 space-y-1 mt-3 mb-3">
                <div className="flex items-center gap-2"><Buildings size={12} className="text-slate-400" /> {pr.site_name}</div>
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center gap-1 ${pr_meta.cls}`}>
                    <span className="text-[10px] uppercase tracking-widest">Öncelik:</span> {pr_meta.label}
                  </span>
                  {pr.need_by_date && (<span className="font-mono text-[11px]">{pr.need_by_date}</span>)}
                </div>
                {pr.created_by_name && (
                  <div className="text-slate-500 text-[11px]">Talep eden: {pr.created_by_name}</div>
                )}
              </div>

              {/* Offers preview */}
              {(pr.offers || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                    Teklifler ({pr.offers.length})
                  </div>
                  <div className="space-y-1.5">
                    {pr.offers.slice(0, 3).map((of, i) => (
                      <div key={i} className={`flex items-center justify-between text-xs p-1.5 border ${pr.selected_offer_index === i && pr.status === "siparis_verildi" ? "bg-[#F7FBE9] border-[#95C11F]" : "bg-slate-50 border-slate-200"}`}>
                        <span className="font-medium truncate flex-1">{of.supplier_name}</span>
                        <span className="font-mono font-bold text-slate-900">₺{of.unit_price.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Created contract link */}
              {pr.created_contract_code && (
                <div className="mt-3 p-2 bg-[#F7FBE9] border border-[#95C11F]/30 text-xs">
                  <span className="text-emerald-700 font-bold">✓ Sözleşme:</span>{" "}
                  <span className="font-mono">{pr.created_contract_code}</span>
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex flex-wrap gap-2">
                {canOffer && (
                  <Button size="sm" variant="outline" data-testid={`add-offer-${pr.id}`}
                          onClick={() => { setOfferFor(pr); setOfferForm(emptyOffer); }}
                          className="rounded-none sm:rounded-sm border-slate-300 h-8 text-xs">
                    <Handshake size={12} className="mr-1" /> Teklif Ekle
                  </Button>
                )}
                {canApprove && (
                  <>
                    <Button size="sm" data-testid={`approve-${pr.id}`}
                            onClick={() => { setApproveFor(pr); setApproveIdx(0); setApproveNotes(""); }}
                            className="rounded-none sm:rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs">
                      <CheckCircle size={12} className="mr-1" /> Onayla
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => reject(pr)}
                            className="rounded-none sm:rounded-sm border-red-300 text-red-700 hover:bg-red-50 h-8 text-xs">
                      <XCircle size={12} className="mr-1" /> Reddet
                    </Button>
                  </>
                )}
              </div>
            </Card>
          );
        })}
        {rows.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500 flex flex-col items-center gap-2">
            <ShoppingBag size={32} weight="duotone" className="text-slate-400" />
            <div>Henüz talep yok.</div>
          </div>
        )}
      </div>

      {/* Offer dialog */}
      <Dialog open={!!offerFor} onOpenChange={(v) => !v && setOfferFor(null)}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-bold font-heading">
              Teklif Yönetimi — {offerFor?.code}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-600 mb-3">
            <strong>{offerFor?.material_name}</strong> — {offerFor?.quantity} {offerFor?.unit}
          </div>

          {/* Existing offers */}
          {(offerFor?.offers || []).length > 0 && (
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                Mevcut Teklifler ({offerFor.offers.length})
              </div>
              <div className="space-y-1">
                {offerFor.offers.map((of, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-slate-50 p-2 border border-slate-200">
                    <div className="flex-1">
                      <div className="font-medium">{of.supplier_name}</div>
                      <div className="text-slate-500 text-[10px]">
                        {of.delivery_days ? `${of.delivery_days} gün teslim` : ""} {of.notes ? `· ${of.notes}` : ""}
                      </div>
                    </div>
                    <span className="font-mono font-bold text-slate-900 mr-2">₺{of.unit_price.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
                    <button onClick={() => delOffer(offerFor.id, i)}
                            className="w-6 h-6 flex items-center justify-center hover:bg-red-50">
                      <TrashSimple size={11} className="text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add offer form */}
          <div className="border-t border-slate-200 pt-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">Yeni Teklif Ekle</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Firma</Label>
                <Select value={offerForm.supplier_id} onValueChange={(v) => {
                  const s = suppliers.find(x => x.id === v);
                  setOfferForm(f => ({ ...f, supplier_id: v, supplier_name: s?.name || "" }));
                }}>
                  <SelectTrigger className="mt-1 rounded-none sm:rounded-sm" data-testid="of-supplier">
                    <SelectValue placeholder="Firma seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Birim Fiyat (₺)</Label>
                <Input type="number" step="0.01" value={offerForm.unit_price} data-testid="of-price"
                       onChange={e => setOfferForm(f => ({ ...f, unit_price: e.target.value }))}
                       className="mt-1 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Teslim Süresi (gün)</Label>
                <Input type="number" value={offerForm.delivery_days}
                       onChange={e => setOfferForm(f => ({ ...f, delivery_days: e.target.value }))}
                       className="mt-1 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Teklif Geçerlilik</Label>
                <Input type="date" value={offerForm.valid_until}
                       onChange={e => setOfferForm(f => ({ ...f, valid_until: e.target.value }))}
                       className="mt-1 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Not</Label>
                <Input value={offerForm.notes}
                       onChange={e => setOfferForm(f => ({ ...f, notes: e.target.value }))}
                       className="mt-1 rounded-none sm:rounded-sm" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={addOffer} data-testid="save-offer"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              Teklifi Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={!!approveFor} onOpenChange={(v) => !v && setApproveFor(null)}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold font-heading">
              Talep Onayı — {approveFor?.code}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm mb-2">
            <div className="font-bold text-slate-900">{approveFor?.material_name}</div>
            <div className="text-slate-600 text-xs">{approveFor?.quantity} {approveFor?.unit} — {approveFor?.site_name}</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700 mb-2 block">Kabul Edilen Teklif</Label>
            <div className="space-y-2">
              {(approveFor?.offers || []).map((of, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setApproveIdx(i)}
                  data-testid={`select-offer-${i}`}
                  className={`w-full text-left p-3 border-2 transition-colors ${approveIdx === i ? "border-[#1B4776] bg-[#F7FBE9]" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold">{of.supplier_name}</span>
                    <span className="font-mono font-bold text-slate-900">₺{of.unit_price.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Toplam: ₺{(of.unit_price * (approveFor?.quantity || 0)).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                    {of.delivery_days ? ` · ${of.delivery_days} gün teslim` : ""}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Onay Notu (opsiyonel)</Label>
            <Textarea value={approveNotes} onChange={e => setApproveNotes(e.target.value)}
                      rows={2} className="mt-1 rounded-none sm:rounded-sm" />
          </div>
          <DialogFooter>
            <Button onClick={approve} data-testid="confirm-approve"
                    className="rounded-none sm:rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle size={14} className="mr-1" /> Onayla ve Sözleşme Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
